import React, { useEffect, useState, useRef, Suspense } from "react";
import { useParams } from "react-router-dom";
import categoryAPI from "../../api/categoryApi";
import SEO from "../../components/SEO";
import ProductCard from "../products/Card";
import { ShoppingBag } from "lucide-react";
import Breadcrump from "../../components/Breadcrump";
import SmartVideoBlock from "../../components/SmartVideoBlock";
import { extractVideosFromProducts } from "../../utils/videoHelper";


const CATEGORY_VIDEOS_MAP = {
  bags: [
    `${process.env.PUBLIC_URL}/assets/videos/bags1.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/bags2.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/bags1.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/bags2.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/bags1.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/bags2.webm`,
  ],
  dresses: [
    `${process.env.PUBLIC_URL}/assets/videos/dress_5.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/dress_2.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/dress_3.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/dress_1.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/dress_4.webm`,
  ],
  woolen: [
    `${process.env.PUBLIC_URL}/assets/videos/woolen1.webm`,
    `${process.env.PUBLIC_URL}/assets/videos/woolen2.webm`,
  ],
};

function DetailPage() {
  const { "*": rawSlug } = useParams();

  const slug = rawSlug ? rawSlug.replace(/^\/+|\/+$/g, "").toLowerCase() : "";

  const [item, setItem] = useState({});
  const [products, setProducts] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(true);

  const staticVideosRef = useRef([]);

  const fetchCategoryDetail = async () => {
    try {
      setLoading(true);
      const res = await categoryAPI.details(rawSlug); 

      if (res.code === 200) {
        const result = res.data;
        setItem(result?.category);
        setProducts(result?.products || []);
        setMetaData(result?.category?.meta);
      }
    } catch (err) {
      console.error("Error loading category details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryDetail();
  }, [rawSlug]);

  // Pehle yaha saari category videos ek saath autoplay ho jaati thi
  // (page load hote hi). Ab har video sirf user ke click/tap (interaction)
  // par play hogi — SmartVideoBlock ka `playOnInteraction` prop yeh sambhalta
  // hai, isliye yeh forced-autoplay effect ab zaroori nahi hai.

  // ==========================================================================
  // 🌟 THE CHUNK MATRIX: Pack elements structured into 4 items grids sequence
  // ==========================================================================
  const productPacks = [];
  const packSize = 4;

  for (let i = 0; i < products.length; i += packSize) {
    productPacks.push(products.slice(i, i + packSize));
  }

  
  const dynamicVideos = extractVideosFromProducts(products);

  if (loading) {
    return (
      <div className="orangeapes-editorial-loader-state text-center py-5 my-5">
        <div
          className="spinner-border spinner-border-sm text-dark"
          role="status"
        />
        <p className="mt-3 tracking-wider text-muted text-sm">
          LOADING COLLECTION ARCHITECTURE...
        </p>
      </div>
    );
  }

  return (
    <>
      <Breadcrump title={item?.name || "Collection"} />
      <SEO meta={metaData} />

      <div className="orangeapes-luxury-collection-wrapper py-5 bg-white detail-page-card-scope">
        <div className="container">
          {productPacks.length > 0 ? (
            <div className="orangeapes-editorial-stream-flow">
              <div className="text-start mb-5">
                <h4 className="orangeapes-collection-main-headline">
                  The Seasonal Drops
                </h4>
              </div>

              {productPacks.map((currentPack, blockIndex) => {
                const currentSlug =
                  slug || item?.slug || item?.name?.toLowerCase() || "";

               
                let videoSrc;
                let usingDynamicVideo = false;

                if (dynamicVideos.length > 0) {
                  videoSrc = dynamicVideos[blockIndex % dynamicVideos.length];
                  usingDynamicVideo = true;
                } else {
                  let targetVideosKey = Object.keys(CATEGORY_VIDEOS_MAP).find(
                    (key) => currentSlug.includes(key),
                  );
                  const currentCategoryVideos =
                    CATEGORY_VIDEOS_MAP[targetVideosKey] ||
                    CATEGORY_VIDEOS_MAP["dresses"];
                  videoSrc =
                    currentCategoryVideos[
                      blockIndex % currentCategoryVideos.length
                    ];
                }

                return (
                  <div
                    className="orangeapes-editorial-group-row mb-5"
                    key={`${currentSlug}-block-row-${blockIndex}`}
                  >
                    {/* CENTERED PORTRAIT VIDEO */}
                    <div className="orangeapes-video-centered-row row justify-content-center mb-5">
                      <div className="col-12 d-flex justify-content-center">
                        <div className="orangeapes-portrait-video-frame">
                          <SmartVideoBlock
                            videoRef={(el) =>
                              (staticVideosRef.current[blockIndex] = el)
                            }
                            className="orangeapes-video-asset-element"
                            src={videoSrc}
                            poster={`${process.env.PUBLIC_URL}/images/blank_image.png`}
                            playOnView
                            lazy
                            key={`${currentSlug}-video-render-${blockIndex}-${usingDynamicVideo}`} // Dynamic key forcing fresh renders
                          />

                          <div className="orangeapes-video-typography-overlay">
                            <span>ORANGEAPES JOURNAL</span>
                            <h5>
                              {item?.name
                                ? `${item.name.toUpperCase()} DROP 0${blockIndex + 1}`
                                : "BOUTIQUE EDITORIAL"}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 🌟 4 PRODUCTS GRID */}
                    <div className="orangeapes-cards-grid-holder-container mb-5">
                      <div className="row g-3 justify-content-start">
                        <Suspense
                          fallback={
                            <div className="col-12 text-center py-3">
                              Aligning boutique mesh...
                            </div>
                          }
                        >
                          {currentPack.map((product, productIdx) => (
                            <div
                              className="col-lg-6 col-6 d-flex align-items-stretch"
                              key={`prod-card-${product._id || productIdx}`}
                            >
                              <ProductCard item={product} />
                            </div>
                          ))}
                        </Suspense>
                      </div>
                    </div>

                    {blockIndex < productPacks.length - 1 && (
                      <hr className="orangeapes-editorial-divider-dash my-5" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="luxury-empty-category-canvas text-center py-5">
              <div className="empty-collection-icon-wrap mb-4">
                <ShoppingBag size={48} strokeWidth={1} color="#999999" />
              </div>
              <h5 className="luxury-empty-collection-title mb-2">
                Collection is Empty
              </h5>
              <p className="luxury-empty-collection-subtitle mb-0">
                We are currently curating new premium arrivals for the{" "}
                <strong>{item?.name || "this"}</strong> category.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default DetailPage;
